/**
 * The help manual, as a printable book.
 *
 * One builder serves all three scopes the lab asked for:
 *   - the whole book  - every page, chaptered by section, with a contents page
 *   - one section     - "Billing", "Client Service", ...
 *   - one page        - the guide for a single screen
 *
 * Printing goes through the browser's own print dialog, where "Save as PDF" is
 * the destination - so the PDF carries selectable text and working links
 * instead of a screenshot of the app.
 */
import { formatLabDate } from './labTime';

const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Accepts either shape and returns the one the book renders from:
 *   - a guide from /help/guides   (title/summary/benefit/features/flows/...)
 *   - a document from /help/pages (overview/functions/flows/...)
 */
export function normalizeGuide(g) {
  if (!g) return null;
  if (g.overview) {
    return {
      slug: g.slug,
      title: g.overview.page_name,
      category: g.category,
      location: g.overview.location,
      summary: g.overview.purpose,
      benefit: g.overview.benefit,
      when_to_use: g.overview.when_to_use,
      features: (g.functions || []).map(f => ({
        name: f.name, description: f.description, benefit: f.benefit, kind: f.kind,
      })),
      flows: (g.flows || []).map(fl => ({
        name: fl.name,
        steps: fl.steps.map((s, i) => ({
          step: i + 1, title: s.title, description: s.description, variation: s.variation,
        })),
      })),
      fields: g.fields || [],
      insider: g.insider || [],
      troubleshoot: g.troubleshoot || [],
    };
  }
  const flows = g.flows?.length
    ? g.flows
    : (g.steps?.length ? [{ name: 'Step-by-Step', steps: g.steps }] : []);
  return { ...g, flows };
}

const CSS = `
  @page { size: letter; margin: 18mm 16mm 20mm; }
  * { box-sizing: border-box; }
  body {
    font-family: "Iowan Old Style", Georgia, "Times New Roman", serif;
    color: #16181d; line-height: 1.62; margin: 0;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .sheet { max-width: 172mm; margin: 0 auto; padding: 0 0 40px; }

  .cover { min-height: 86vh; display: flex; flex-direction: column; justify-content: center; }
  .cover .rule { width: 68px; height: 4px; background: #1f4fd8; margin-bottom: 26px; }
  .cover h1 { font-size: 46px; line-height: 1.08; margin: 0 0 12px; letter-spacing: -0.02em; }
  .cover .sub { font-size: 17px; color: #4b5563; margin: 0 0 34px; max-width: 118mm; }
  .cover .meta { font-family: ui-sans-serif, -apple-system, "Segoe UI", sans-serif;
                 font-size: 11px; letter-spacing: .09em; text-transform: uppercase; color: #6b7280; }
  .cover .meta b { color: #16181d; }

  .contents { break-before: page; }
  .contents h2 { font-size: 24px; margin: 0 0 20px; }
  .toc-chapter { margin-bottom: 18px; break-inside: avoid; }
  .toc-chapter > div:first-child {
    font-family: ui-sans-serif, -apple-system, "Segoe UI", sans-serif;
    font-size: 11px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
    color: #1f4fd8; padding-bottom: 5px; border-bottom: 1px solid #dfe3ea; margin-bottom: 7px;
  }
  .toc-line { display: flex; align-items: baseline; gap: 8px; font-size: 13.5px; padding: 2px 0; }
  .toc-line a { color: #16181d; text-decoration: none; }
  .toc-line .dots { flex: 1; border-bottom: 1px dotted #c3c9d4; transform: translateY(-3px); }
  .toc-line .where { font-family: ui-monospace, Menlo, monospace; font-size: 10.5px; color: #8b93a1; }

  .chapter-head { break-before: page; padding-top: 6mm; }
  .chapter-head .eyebrow {
    font-family: ui-sans-serif, -apple-system, "Segoe UI", sans-serif;
    font-size: 11px; font-weight: 700; letter-spacing: .11em; text-transform: uppercase; color: #1f4fd8;
  }
  .chapter-head h2 { font-size: 32px; margin: 6px 0 4px; letter-spacing: -0.015em; }
  .chapter-head .count { font-size: 13px; color: #6b7280; }

  .doc { break-before: page; padding-top: 4mm; }
  .doc .where { font-family: ui-monospace, Menlo, monospace; font-size: 10.5px; color: #8b93a1; }
  .doc h3 { font-size: 25px; margin: 3px 0 8px; letter-spacing: -0.015em; break-after: avoid; }
  .doc .summary { font-size: 15px; margin: 0 0 12px; }
  .why { border-left: 3px solid #15803d; background: #f2fbf5; padding: 10px 14px;
         font-size: 13.5px; margin: 0 0 14px; break-inside: avoid; }
  .why b { color: #15803d; }
  .when { font-size: 13.5px; color: #4b5563; margin: 0 0 18px; }

  h4 {
    font-family: ui-sans-serif, -apple-system, "Segoe UI", sans-serif;
    font-size: 11px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
    color: #6b7280; margin: 22px 0 9px; padding-bottom: 5px; border-bottom: 1px solid #e5e8ee;
    break-after: avoid;
  }

  .feature { padding: 9px 0; border-bottom: 1px solid #f0f2f6; break-inside: avoid; }
  .feature:last-child { border-bottom: none; }
  .feature .n { font-weight: 700; font-size: 14px; }
  .feature .k { font-family: ui-sans-serif, sans-serif; font-size: 9px; font-weight: 700;
                text-transform: uppercase; letter-spacing: .05em; color: #6b7280;
                background: #eef1f6; padding: 1px 6px; border-radius: 4px; margin-left: 6px; }
  .feature .d { font-size: 13.5px; color: #333840; margin-top: 2px; }
  .feature .b { font-size: 12.5px; color: #15803d; margin-top: 4px; }

  .flow { margin-bottom: 16px; break-inside: avoid-page; }
  .flow .fname { font-size: 15px; font-weight: 700; margin-bottom: 6px; break-after: avoid; }
  .step { display: flex; gap: 11px; padding: 7px 0; break-inside: avoid; }
  .step .num { flex: 0 0 22px; height: 22px; border-radius: 50%; background: #1f4fd8; color: #fff;
               font-family: ui-sans-serif, sans-serif; font-size: 11px; font-weight: 700;
               display: flex; align-items: center; justify-content: center; }
  .step .t { font-weight: 700; font-size: 13.5px; }
  .step .v { font-family: ui-sans-serif, sans-serif; font-size: 9px; text-transform: uppercase;
             color: #92400e; background: #fef3c7; padding: 1px 6px; border-radius: 4px; margin-left: 6px; }
  .step .d { font-size: 13px; color: #333840; }

  table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  th { font-family: ui-sans-serif, sans-serif; text-align: left; font-size: 9.5px; font-weight: 700;
       text-transform: uppercase; letter-spacing: .06em; color: #6b7280;
       border-bottom: 1.5px solid #d7dbe3; padding: 6px 8px; }
  td { border-bottom: 1px solid #eef0f4; padding: 7px 8px; vertical-align: top; }
  tr { break-inside: avoid; }
  .req { font-family: ui-sans-serif, sans-serif; font-size: 8.5px; font-weight: 700;
         text-transform: uppercase; color: #b91c1c; margin-left: 5px; }

  .note { border-left: 3px solid #d97706; background: #fffbeb; padding: 9px 13px;
          margin-bottom: 8px; font-size: 13px; break-inside: avoid; }
  .note b { display: block; }
  .ts { border-left: 3px solid #b91c1c; background: #fef4f4; padding: 9px 13px;
        margin-bottom: 8px; font-size: 13px; break-inside: avoid; }
  .ts .cause { color: #6b7280; font-size: 12.5px; margin: 2px 0 4px; }
  .ts ol { margin: 0; padding-left: 18px; }

  .colophon { break-before: page; padding-top: 30mm; font-size: 12px; color: #6b7280; }

  @media screen {
    body { background: #f3f4f6; padding: 28px 0; }
    .sheet { background: #fff; padding: 26mm 20mm; box-shadow: 0 2px 20px rgba(0,0,0,.12); }
  }
  @media print { .no-print { display: none !important; } }
`;

function renderDoc(g, opts) {
  const withAnchor = !opts || opts.withAnchor !== false;
  const id = withAnchor ? ` id="doc-${esc(g.slug || slugify(g.title))}"` : '';
  let h = `<article class="doc"${id}>`;
  if (g.location) h += `<div class="where">${esc(g.location)}</div>`;
  h += `<h3>${esc(g.title)}</h3>`;
  h += `<p class="summary">${esc(g.summary)}</p>`;
  if (g.benefit) h += `<div class="why"><b>Why it matters.</b> ${esc(g.benefit)}</div>`;
  if (g.when_to_use) h += `<p class="when"><b>When to use it.</b> ${esc(g.when_to_use)}</p>`;

  if (g.features?.length) {
    h += `<h4>What it does, and why that helps</h4>`;
    for (const f of g.features) {
      h += `<div class="feature"><div class="n">${esc(f.name)}`
         + (f.kind ? `<span class="k">${esc(f.kind)}</span>` : '')
         + `</div><div class="d">${esc(f.description)}</div>`
         + (f.benefit ? `<div class="b">&rarr; ${esc(f.benefit)}</div>` : '')
         + `</div>`;
    }
  }

  if (g.flows?.length) {
    h += `<h4>Step by step</h4>`;
    for (const fl of g.flows) {
      h += `<div class="flow"><div class="fname">${esc(fl.name)}</div>`;
      for (const s of fl.steps) {
        h += `<div class="step"><div class="num">${esc(s.step)}</div><div>`
           + `<div class="t">${esc(s.title)}`
           + (s.variation && s.variation !== 'basic' ? `<span class="v">${esc(s.variation)}</span>` : '')
           + `</div><div class="d">${esc(s.description)}</div></div></div>`;
      }
      h += `</div>`;
    }
  }

  if (g.fields?.length) {
    h += `<h4>Field reference</h4><table><thead><tr>`
       + `<th style="width:24%">Field</th><th>What it means</th><th style="width:26%">Accepted values</th>`
       + `</tr></thead><tbody>`;
    for (const f of g.fields) {
      h += `<tr><td><b>${esc(f.name)}</b>${f.required ? '<span class="req">required</span>' : ''}</td>`
         + `<td>${esc(f.meaning)}`
         + (f.examples?.length ? `<div style="color:#6b7280">e.g. ${esc(f.examples.join(', '))}</div>` : '')
         + `</td><td>${esc(f.valid_values || '—')}</td></tr>`;
    }
    h += `</tbody></table>`;
  }

  if (g.insider?.length) {
    h += `<h4>Worth knowing</h4>`;
    for (const i of g.insider) h += `<div class="note"><b>${esc(i.topic)}</b>${esc(i.detail)}</div>`;
  }

  if (g.troubleshoot?.length) {
    h += `<h4>When something goes wrong</h4>`;
    for (const t of g.troubleshoot) {
      h += `<div class="ts"><b>${esc(t.symptom)}</b>`
         + `<div class="cause">${esc(t.cause)}</div><ol>`
         + (t.fix || []).map(f => `<li>${esc(f)}</li>`).join('')
         + `</ol></div>`;
    }
  }
  return h + `</article>`;
}

/**
 * @param {Array|Object} rawGuides  guides or documents, in either shape
 * @param {Object} opts   scope: 'book' | 'section' | 'page', title, labName
 */
export function buildHelpBook(rawGuides, opts = {}) {
  const { scope = 'book', title, labName = '' } = opts;
  const guides = (Array.isArray(rawGuides) ? rawGuides : [rawGuides]).map(normalizeGuide).filter(Boolean);
  const now = formatLabDate();

  const chapters = new Map();
  for (const g of guides) {
    const c = g.category || 'General';
    if (!chapters.has(c)) chapters.set(c, []);
    chapters.get(c).push(g);
  }
  for (const list of chapters.values()) list.sort((a, b) => a.title.localeCompare(b.title));
  const chapterNames = [...chapters.keys()].sort();

  const heading = title || (scope === 'page' ? guides[0]?.title
                          : scope === 'section' ? chapterNames[0]
                          : 'User Manual');
  const subtitle = scope === 'section'
    ? `Every screen in ${chapterNames[0] || 'this section'} — what it does, why it helps, and the steps to follow.`
    : 'Every screen in the system — what it does, why it helps, and the steps to follow.';

  let html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">`
           + `<title>${esc(heading)}</title><style>${CSS}</style></head><body><div class="sheet">`;

  // A single page prints as itself - a cover and a contents page in front of
  // one screen's instructions would be ceremony, not help.
  if (scope === 'page') {
    html += renderDoc(guides[0], { withAnchor: false });
  } else {
    html += `<section class="cover"><div class="rule"></div>`
          + `<h1>${esc(heading)}</h1><p class="sub">${esc(subtitle)}</p>`
          + `<div class="meta">`
          + (labName ? `${esc(labName)} &nbsp;&middot;&nbsp; ` : '')
          + `<b>${guides.length}</b> page${guides.length === 1 ? '' : 's'}`
          + ` &nbsp;&middot;&nbsp; <b>${chapterNames.length}</b> section${chapterNames.length === 1 ? '' : 's'}`
          + ` &nbsp;&middot;&nbsp; ${esc(now)}</div></section>`;

    html += `<section class="contents"><h2>Contents</h2>`;
    for (const c of chapterNames) {
      html += `<div class="toc-chapter"><div>${esc(c)}</div>`;
      for (const g of chapters.get(c)) {
        html += `<div class="toc-line"><a href="#doc-${esc(g.slug || slugify(g.title))}">${esc(g.title)}</a>`
              + `<span class="dots"></span><span class="where">${esc(g.location || '')}</span></div>`;
      }
      html += `</div>`;
    }
    html += `</section>`;

    for (const c of chapterNames) {
      const list = chapters.get(c);
      if (chapterNames.length > 1) {
        html += `<section class="chapter-head"><div class="eyebrow">Section</div>`
              + `<h2>${esc(c)}</h2>`
              + `<div class="count">${list.length} page${list.length === 1 ? '' : 's'}</div></section>`;
      }
      for (const g of list) html += renderDoc(g);
    }

    html += `<section class="colophon">Generated ${esc(now)}.`
          + ` This manual is produced from the system's own help content, so it describes the software as it`
          + ` is configured today. Reprint it after a release rather than correcting it by hand.</section>`;
  }

  return html + `</div></body></html>`;
}

/** Open the print dialog - the reader picks "Save as PDF" there. */
export function printHelp(html) {
  const win = window.open('', '_blank');
  if (!win) { alert('Allow pop-ups for this site to print the manual.'); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  const go = () => setTimeout(() => win.print(), 400);
  if (win.document.readyState === 'complete') go();
  else win.onload = go;
}

/** Save the same book as a self-contained HTML file. */
export function downloadHelp(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const bookFilename = (heading) => `${slugify(heading) || 'help'}-manual.html`;
